import type { Table } from '@tanstack/react-table'
import { FileCogIcon, TriangleAlertIcon } from 'lucide-react'
import { useState } from 'react'

import { Button, type ButtonProps, Tooltip } from '@/components/ui'
import { useToast } from '@/hooks'
import { useSummary } from '@/modules/account'
import { MonthlyLimitExceededError } from '@/modules/errors'
import { cn } from '@/utils'
import { useProjectMutations } from '../../hooks'
import type { Statement } from '../../stores'
import { CreditsMessage } from './CreditsMessage'
import { useProcessButtonState } from './hooks'
import { LegacyMessage } from './LegacyMessage'
import { SingleProjectMessage } from './SingleProjectMessage'

interface ProcessButtonProps extends ButtonProps {
  table: Table<any> | null
  icon?: React.ComponentType<{ className?: string }>
  statementsToProcess?: Statement[]
  validationBehaviour?: 'message' | 'hidden' | 'disable-only'
  messagePlacement?: 'overlay' | 'block'
  children?: React.ReactNode
}

const AlertIcon = () => <TriangleAlertIcon className="text-amber-400 size-4" />
export const ProcessButton = ({
  table,
  statementsToProcess,
  children,
  onClick,
  icon,
  disabled,
  validationBehaviour = 'message',
  messagePlacement = 'overlay',
  ...props
}: ProcessButtonProps) => {
  const { successToast, errorToast } = useToast()
  const summary = useSummary()
  const { extractAndEnrichStatements } = useProjectMutations()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { isBlocked, isProcessingDisabled, disabledReason } =
    useProcessButtonState(statementsToProcess, disabled)

  const Icon = summary && isBlocked ? AlertIcon : (icon ?? FileCogIcon)

  const triggerProcess = async () => {
    table?.resetRowSelection()
    const ids = statementsToProcess
      ? statementsToProcess.map(({ id }) => id)
      : undefined

    setIsSubmitting(true)
    try {
      await extractAndEnrichStatements(ids)
      successToast('Process statements started')
    } catch (e) {
      const error = e as Error
      let title = 'Failed to process statements'
      if (error instanceof MonthlyLimitExceededError) {
        title = 'Monthly Limit reached'
      }

      errorToast(title, error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isBlocked && validationBehaviour === 'hidden') return

  const isOverlay = messagePlacement === 'overlay'

  return (
    <span
      className={cn(
        isOverlay ? 'relative' : 'flex flex-col',
        isOverlay &&
          summary &&
          isBlocked &&
          validationBehaviour === 'message' &&
          'pb-6',
      )}
    >
      {/* A disabled button swallows pointer events, so the tooltip hangs off a wrapping span. */}
      <Tooltip content={isProcessingDisabled ? disabledReason : null}>
        <span className="inline-flex">
          <Button
            isLoading={isSubmitting}
            loadingText="Processing..."
            disabled={isProcessingDisabled}
            onClick={(e) => {
              triggerProcess()
              onClick?.(e)
            }}
            {...props}
          >
            <Icon />
            {children}
          </Button>
        </span>
      </Tooltip>

      <span
        className={
          isOverlay
            ? 'absolute bottom-0 right-0 mt-1 min-w-max'
            : 'mt-2 empty:mt-0'
        }
      >
        <LegacyMessage
          isBlocked={!!summary && isBlocked}
          validationBehaviour={validationBehaviour}
        />
        <CreditsMessage
          isBlocked={!!summary && isBlocked}
          validationBehaviour={validationBehaviour}
        />
        <SingleProjectMessage
          isBlocked={isBlocked}
          validationBehaviour={validationBehaviour}
        />
      </span>
    </span>
  )
}
